// Vertical / industry data — feed for /industries and /industries/:slug.
// Each entry drives a full landing page.

export const VERTICALS = [
  {
    slug: "marine",
    name: "Marine",
    tagline: "Pontoons, jet-skis, kayaks, fishing charters.",
    headline: "Run your marina like an operator, not a spreadsheet jockey.",
    sub: "Seasonal demand, safety waivers, mixed fleets. Built for liveries and marinas that need their software to bend to the water — not the other way around.",
    accent: "from-sky-500/15 to-cyan-400/0",
    examples: [
      "Pontoons & ski boats",
      "Jet-skis & PWC",
      "Kayaks, canoes, SUPs",
      "Fishing charters",
      "Sailing lessons",
      "Tow tubes & gear add-ons",
    ],
    features: [
      "Hourly, half-day and multi-day duration tiers per boat class",
      "Built-in digital waivers + age/license capture at checkout",
      "Seasonal calendars with weather-driven closures",
      "Photo check-in/out from the dock on your phone",
      "Fuel + add-on charges captured post-trip via integrated POS",
    ],
    useCase: {
      title: "Lakeside Marina, 22 units across 3 boat classes",
      body: "Before RentAnything: paper waivers, Excel slots, and double-bookings every July. After 30 days: 100% digital check-in/out, $14k in add-on revenue captured at return, and Saturdays sold out the night before.",
    },
  },
  {
    slug: "equipment",
    name: "Equipment",
    tagline: "Tools, machinery, construction & event gear.",
    headline: "Same-day to multi-week — one platform, every rate card.",
    sub: "Skid-steers and scissor lifts don't rent like kayaks. Configure deposits, damage waivers, mileage caps and operator certifications without forking the product.",
    accent: "from-amber-400/15 to-amber-200/0",
    examples: [
      "Skid-steers, lifts, excavators",
      "Power tools & generators",
      "Trailers & haulers",
      "Event/party rentals",
      "AV & lighting gear",
      "Long-term contractor accounts",
    ],
    features: [
      "Per-day, per-week, per-month tiered pricing with auto-rollover",
      "Required operator credentials uploaded at checkout",
      "Damage deposits, fuel charges, and odometer reads",
      "Contractor accounts with net-terms billing",
      "Delivery vs. pickup zones with mileage-based fees",
    ],
    useCase: {
      title: "Regional equipment yard, 140 SKUs",
      body: "Switched from a 2003 rental ERP. Cut onboarding for a new branch from 6 weeks to 4 days. Contractors self-serve reservations from their truck.",
    },
  },
  {
    slug: "recreation",
    name: "Recreation",
    tagline: "Bikes, skis, snowboards, climbing gear.",
    headline: "Sized, fit, and gone in under a minute.",
    sub: "Recreation rentals live and die on speed at the counter. Capture rider weight, height and skill at booking so your shop never re-measures on a busy morning.",
    accent: "from-emerald-400/15 to-emerald-200/0",
    examples: [
      "Ski & snowboard packages",
      "Mountain & road bikes",
      "Surf, paddle, climb gear",
      "E-bike fleets",
      "Snowshoes & winter sets",
      "Group lessons + gear bundles",
    ],
    features: [
      "Sizing intake (height/weight/foot) captured at booking",
      "Gear bundles (board + boots + helmet) priced as one",
      "Resort-day calendars with peak/holiday auto-pricing",
      "QR scan on hand-off — no clipboards",
      "Damage and stolen-gear waivers built in",
    ],
    useCase: {
      title: "Alpine ski rental, 800-pair fleet",
      body: "Mornings used to start at 6am. Now sizing is captured the night before from the renter's phone — staff stage gear, not data entry.",
    },
  },
  {
    slug: "powersports",
    name: "Powersports",
    tagline: "ATVs, UTVs, dirt bikes, snowmobiles.",
    headline: "High-value units, high-risk waivers — handled.",
    sub: "Six-figure fleets demand serious deposits, identity capture, and clear damage policy. RentAnything bakes the compliance into checkout so your front desk doesn't have to.",
    accent: "from-rose-400/15 to-rose-200/0",
    examples: [
      "ATVs & UTVs",
      "Dirt bikes & motocross",
      "Snowmobiles",
      "Side-by-sides",
      "Guided trail packages",
      "Trailer-loadout add-ons",
    ],
    features: [
      "ID + license image capture at booking, stored encrypted",
      "Tiered security deposits by unit value",
      "GPS unit tracking with geofenced alerts",
      "Pre-ride safety video acknowledgement",
      "Damage estimator with photo evidence pinned to rental",
    ],
    useCase: {
      title: "Mountain UTV outfitter, 38 units",
      body: "Reduced disputed damage claims by 80% in one season — every chip and scrape is photo-stamped at pickup and return inside the rental record.",
    },
  },
  {
    slug: "tours",
    name: "Tours & Experiences",
    tagline: "Guided trips, classes, multi-stop adventures.",
    headline: "Capacity, time-slots, and guides — one calendar.",
    sub: "When the product is a moment in time, double-booking a guide is fatal. Capacity rules, blackout days and per-guide schedules live in the same engine as the inventory.",
    accent: "from-violet-400/15 to-violet-200/0",
    examples: [
      "Sunset & dinner cruises",
      "Brewery & winery tours",
      "Guided hikes & climbs",
      "Cooking & art classes",
      "Multi-day expeditions",
      "Corporate offsites",
    ],
    features: [
      "Time-slot scheduling with per-guide assignment",
      "Capacity rules, waitlists, and dynamic upsells",
      "Per-participant intake (allergies, sizes, contacts)",
      "Group bookings with split payment links",
      "Automated reminders + photo follow-up after the trip",
    ],
    useCase: {
      title: "Coastal sunset cruise, 4 vessels, 6 daily slots",
      body: "Sold out a peak month two weeks early by surfacing waitlist demand and auto-adding a 5pm sailing once 80% capacity hit.",
    },
  },
  {
    slug: "events",
    name: "Events",
    tagline: "Tents, tables, AV, photo booths, weekend rentals.",
    headline: "Quote, contract, deliver, return — without a six-tab spreadsheet.",
    sub: "Event rentals run on quotes and contracts, not impulse checkouts. Convert quotes to bookings, lock crew, route delivery trucks, and reconcile damages — all under one record.",
    accent: "from-pink-400/15 to-pink-200/0",
    examples: [
      "Tents, tables, chairs",
      "Photo booths & DJ rigs",
      "Stage, lighting, AV",
      "Linens & tabletop",
      "Generators & climate",
      "Wedding & corporate packages",
    ],
    features: [
      "Quote → contract → deposit flow with e-sign",
      "Delivery routing and crew assignment per job",
      "Bundle SKUs (tent + tables + chairs as one line)",
      "Damaged/lost reconciliation against deposit",
      "Multi-day holds for weekend installs",
    ],
    useCase: {
      title: "Regional event rental, 1,200 SKUs",
      body: "Standardised 14 different quote templates into one configurable engine. Crew sees the day's loadout on their phone Friday night.",
    },
  },
];

export const findVertical = (slug) => VERTICALS.find((v) => v.slug === slug);
